import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import InterventionElementContainer from "../InterventionElementContainer";
import InterventionElement from "../InterventionElement";
import { useDispatch, useSelector } from "react-redux";
import {
  changeInterventionPlanOrder,
  changeInterventionPlanOrderNew,
  checkedInterventionPlan,
  sortInterventionsByInterventionPlan,
} from "../../../../../../../utils";
import {
  hideInterventionPlanModal,
  openInterventionLibraryModal,
  patchInterventionGroup,
  showInterventionPlanModal,
} from "../../../../../../../../store/onboarding/cases/actions";
import {
  Intervention,
  InterventionGroup,
  InterventionPlan,
  InterventionPlanItem,
} from "../../../../../../../../store/onboarding/cases/types";
import WorkspaceHeader from "./WorkspaceHeader";
import { ApplicationState } from "../../../../../../../../store";
import { GroupWorkSpace } from "./WorkspacesContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import useInterventionGroupAccess from "../../../../../../../../utils/hooks/useInterventionGroupAccess";
import { closestCenter, DndContext, DragEndEvent, DragOverEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

type Props = {
  expanded?: boolean;
  workspace: GroupWorkSpace;
  interventionGroup: InterventionGroup;
  setExpandedWorkSpace: React.Dispatch<
    React.SetStateAction<GroupWorkSpace | undefined>
  >;
};

const InterventionPlanWorkspace: FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  const {
    expanded,
    workspace,
    interventionGroup,
    setExpandedWorkSpace,
  } = props;
  const { interventions } = interventionGroup;
  const isLoading = useSelector<ApplicationState, boolean>(
    (s) =>
      s.cases.isLoading.patchInterventionGroup ||
      s.cases.isLoading.addInterventionsToGroup
  );
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require pointer to move 8px before drag starts — prevents accidental drags on clicks
      activationConstraint: { distance: 8 },
    })
  );

  const isStudentDetail =  useSelector(
    (s: ApplicationState) => s.cases.isStudentDetail
  );

  const isGroupDetail =  useSelector(
    (s: ApplicationState) => s.cases.isGroupDetail
  );

  const { isIntGrpIDsWithReadAccess } = useInterventionGroupAccess(interventionGroup.id);

  const [statePlan, setStatePlan] = useState<InterventionPlan>(
    checkedInterventionPlan(interventions, interventionGroup.intervention_plan, "interventionWorkspacePlan")
  );

  const sortInterventionsByPlanItems = (
    interventions: Intervention[],
    planItems?: InterventionPlanItem[]
  ): Intervention[] => {
    if (!planItems?.length) return interventions;

    return [...interventions].sort((a, b) => {
      const aIndex = planItems.findIndex((pi) => pi.intervention_id === a.id);
      const bIndex = planItems.findIndex((pi) => pi.intervention_id === b.id);

      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });
  };

  useEffect(() => {
    const updatedPlan = checkedInterventionPlan(
      interventions,
      interventionGroup.intervention_plan,
      "interventionWorkspacePlan"
    );

    setStatePlan(updatedPlan);
    
    setInterventionItems(sortInterventionsByPlanItems(
      interventions,
      interventionGroup.intervention_plan?.plan_items
    ))
  }, [interventions, interventionGroup.intervention_plan]);

  const hideParentDelete = (interventionId:number) => {
    for (let i = 0; i < interventions.length; i++) {
      if(interventionId == interventions[i].forked_from?.id) return true
    }
    return false
  }

  // const onDragInterventionUpdate = (result: DropResult) => {
  //   const { source, destination, draggableId } = result;
  //   if (destination && destination.droppableId === source.droppableId) {
  //     const changedPlan = changeInterventionPlanOrder(
  //       interventionGroup.intervention_plan,
  //       interventions,
  //       source.index,
  //       destination.index,
  //       draggableId
  //     );
  //     setStatePlan(changedPlan);
  //   }
  // };

  // const onDragInterventionEnd = (result: DropResult) => {
  //   const { source, destination } = result;
  //   if (
  //     destination &&
  //     destination.droppableId === source.droppableId &&
  //     destination.index !== source.index
  //   ) {
  //     dispatch(
  //       patchInterventionGroup(interventionGroup.id, {
  //         intervention_plan: statePlan,
  //       })
  //     );
  //   }
  // };


   const sortedInterventions = useMemo(
    () => sortInterventionsByInterventionPlan(interventions, statePlan),
    [interventions, statePlan]
  );

  const [interventionItems, setInterventionItems] = useState(sortedInterventions);
  const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);

  const onDragStart = (event: DragStartEvent) => {
    const index = sortedInterventions.findIndex(
      i => i.id.toString() === event.active.id
    );

    setDragStartIndex(index);
  };

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = interventionItems.findIndex(
      i => i.id.toString() === active.id
    );

    const newIndex = interventionItems.findIndex(
      i => i.id.toString() === over.id
    );

    if (oldIndex === -1 || newIndex === -1) return;

  };

  // Mirror of react-beautiful-dnd's onDragEnd — persists new order via dispatch
  const onDragEnd = (event: DragEndEvent) => {
  const { active, over } = event;

  if (!over) {
    return;
  }

  const oldIndex = interventionItems.findIndex(
    i => i.id.toString() === active.id
  );

  const newIndex = interventionItems.findIndex(
    i => i.id.toString() === over.id
  );

  if (oldIndex === -1 || newIndex === -1) {
    return;
  }
  if(oldIndex == newIndex) {
    return;
  }
  const reordered = arrayMove(interventionItems, oldIndex, newIndex);

  setInterventionItems(reordered);

  const changedPlan = checkedInterventionPlan(
    reordered,
    statePlan,
    "interventionWorkspacePlan"
  );

  setStatePlan(changedPlan);

  dispatch(
    patchInterventionGroup(interventionGroup.id, {
      intervention_plan: changedPlan,
    })
  );
};

  const sortedIds = interventionItems.map((i) => i.id.toString());
  
  const handleInterventionAdd = () => {
    //dispatch(hideInterventionPlanModal());
    dispatch(
      openInterventionLibraryModal()
    );
  };

  const isDragPermitted = !isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess;

  return (
    <div
      className="d-flex flex-column intervention-plan-list "
      style={isLoading ? { opacity: ".4" } : undefined}
    >
      <WorkspaceHeader
        expanded={expanded}
        workspace={workspace}
        setExpandedWorkSpace={setExpandedWorkSpace}
        isLoading={isLoading}
        header={
          <div>
            Selected Interventions{" "}
            {!isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess && <span onClick={handleInterventionAdd}>
              <FontAwesomeIcon icon={faPlusCircle} />
            </span>}
          </div>
        }
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={sortedIds}
          strategy={verticalListSortingStrategy}
          disabled={isLoading || !isDragPermitted}
        >
          <div className="intervention-plan-active">
            {interventionItems.map((intervention, index) => (
              <InterventionElement
                key={intervention.id}
                index={index}
                isLoading={isLoading || (urlTab && ['archived', 'exited_groups'].includes(urlTab)) || interventionItems.length <= 1}
                intervention={intervention}
                interventionGroup={interventionGroup}
                statePlan={statePlan}
                setStatePlan={setStatePlan}
                hideDelete={hideParentDelete(intervention.id)}
                isStudentDetail={isStudentDetail}
                isGroupDetail={isGroupDetail}
                interventionCount={interventionItems.length}
                isDragPermitted={isDragPermitted}
              />
            ))}
          </div>
        </SortableContext>
        {interventionItems.length === 0 && !isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess &&(
          <div className="workspace-body text-center">
            <div>
              <button
                className="blueBtnSm"
                onClick={handleInterventionAdd}
              >
                Add First Intervention
              </button>
            </div>
          </div>
        )}
      </DndContext>
      {/* <DragDropContext
        onDragUpdate={onDragInterventionUpdate}
        onDragEnd={onDragInterventionEnd}
      >
        <Droppable
          droppableId={interventionGroup.id.toString()}
          isDropDisabled={isLoading}
        >
          {(provided) => (
            <div
              className="intervention-plan-active"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {sortedInterventions.map((intervention, index) => (
                <Draggable
                  key={intervention.id}
                  draggableId={intervention.id.toString()}
                  isDragDisabled={isLoading}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <InterventionElementContainer
                      usePortal={snapshot.isDragging}
                    >
                      <InterventionElement
                        index={index}
                        isLoading={isLoading}
                        provided={provided}
                        statePlan={statePlan}
                        intervention={intervention}
                        interventionGroup={interventionGroup}
                        setStatePlan={setStatePlan}
                        hideDelete={hideParentDelete(intervention.id)}
                        isStudentDetail = {isStudentDetail}
                        isGroupDetail = {isGroupDetail}
                      />
                    </InterventionElementContainer>
                  )}
                </Draggable>
              ))}
              {sortedInterventions.length === 0 && !isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess &&(
                <div className="workspace-body text-center">
                  <div>
                    <button
                      className="blueBtnSm"
                      onClick={handleInterventionAdd}
                    >
                      Add First Intervention
                    </button>
                  </div>
                </div>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext> */}
    </div>
  );
};

export default InterventionPlanWorkspace;
